import * as PIXI from "pixi.js";
import { TextTitlePair } from "./TextTitlePair";
import { BetPopup } from "./BetPopup";
import { CheatPopup } from "./CheatPopup";
import { AutoplayPopup } from "./AutoplayPopup";
import { GameEngine } from "src/server/GameEngine";
import { GlobalState } from "src/shared/util/GlobalState";
import { SpinButton } from "./SpinButton";
import { GameAssets } from "src/shared/util/GameAssets";
import { AutoplayButton } from "./AutoplayButton";
import { observer } from "src/shared/util/Observer";
import { BetButton } from "./BetButton";
import { FastButton } from "./FastButton";
import { SoundButton } from "./SoundButton";
import { InfoPopup } from "./InfoPopup";
import AppSingleton from "src/shared/util/AppSingleton";

// Define an enum for the states
export enum UIState {
  SPIN = "SPIN",
  STOP = "STOP",
  PASSIVE = "PASSIVE",
  BET_POPUP = "BET_POPUP",
  AUTOPLAY_POPUP = "AUTOPLAY_POPUP",
  INFO_POPUP = "INFO_POPUP",
  CHEAT_POPUP = "CHEAT_POPUP",
  LOCK = "LOCK",
}

export enum UIEvent {
  SCREEN_CLICK = "SCREEN_CLICK",
}

export class UI extends PIXI.Container {
  private content!: PIXI.Container;
  private menuBar!: PIXI.Container;
  private balanceContent!: TextTitlePair;
  private winContent!: TextTitlePair;
  private betPopup!: BetPopup;
  private cheatPopup!: CheatPopup;
  private autoplayPopup!: AutoplayPopup;

  private selectBetButton: BetButton;
  private cheatButton: PIXI.Sprite;
  private buyButton: PIXI.Sprite;
  private menuButton: PIXI.Sprite;
  private homeButton: PIXI.Sprite;
  private infoButton: PIXI.Sprite;
  private questionButton: PIXI.Sprite;
  private spinButton: SpinButton;
  private autoplayButton: AutoplayButton;
  private fastButton: FastButton;
  private soundButton: SoundButton;
  private bottomBar: PIXI.Sprite;
  private screenInteractiveArea: PIXI.Graphics;
  private bottomBlackCoverArea: PIXI.Graphics;

  private currentState: UIState = UIState.LOCK;
  private isFreespins: boolean = false;
  private autoplayAmount: number = 0;
  private buttonClickEvent: CustomEvent;
  private specialButtonClickEvent: CustomEvent;

  constructor() {
    super();
    this.buttonClickEvent = new CustomEvent("buttonClicked");
    this.specialButtonClickEvent = new CustomEvent("specialButtonClicked");

    // Create a fullscreen interactive object
    this.screenInteractiveArea = new PIXI.Graphics();
    this.screenInteractiveArea.rect(-960, -960, 1920, 1920);
    this.screenInteractiveArea.fill({ color: 0xffffff, alpha: 1 });
    this.screenInteractiveArea.interactive = true; // Enable interaction
    this.screenInteractiveArea.alpha = 0;
    this.screenInteractiveArea.zIndex = 0;

    this.bottomBlackCoverArea = new PIXI.Graphics();
    this.bottomBlackCoverArea.zIndex = 1;

    // Add to the stage
    AppSingleton.getInstance().app.stage.addChild(this.screenInteractiveArea);
    AppSingleton.getInstance().app.stage.addChild(this.bottomBlackCoverArea);
    this.screenInteractiveArea.on("pointerup", this.onScreenClick, this);

    window.addEventListener("keyup", this.handleKeyDown.bind(this));

    this.menuBar = new PIXI.Container();
    this.addChild(this.menuBar);

    this.bottomBar = new PIXI.Sprite(
      GameAssets.textures.uiAtlas.textures["bottomBar.webp"],
    );
    this.bottomBar.anchor.set(0.5);
    this.menuBar.addChild(this.bottomBar);

    AppSingleton.getInstance().app.stage.on("resize", this.resize, this);
    this.content = new PIXI.Container();
    this.addChild(this.content);

    const spacing: number = 20;
    this.balanceContent = new TextTitlePair(
      "Balance",
      GameEngine.getInstance()
        .getFormatter()
        .format(GlobalState.getInstance().balance),
      30,
      spacing,
    );
    this.menuBar.addChild(this.balanceContent);

    this.winContent = new TextTitlePair(
      "Win",
      GameEngine.getInstance().getFormatter().format(0),
      30,
      spacing,
    );
    this.menuBar.addChild(this.winContent);

    this.autoplayButton = new AutoplayButton();
    this.autoplayButton.on("autoplay", this._onAutoplayClick, this);

    this.fastButton = new FastButton();

    this.soundButton = new SoundButton();

    this.spinButton = new SpinButton();
    this.spinButton.on("spin", this._onSpin, this);
    this.spinButton.on("autoplayStart", this._onAutoplayStart, this);
    this.spinButton.on("autoplayStop", this._onAutoplayStop, this);

    this.selectBetButton = new BetButton();
    this.selectBetButton.setBet(
      GameEngine.getInstance()
        .getFormatter()
        .format(GlobalState.getInstance().bet),
    );
    this.selectBetButton.on("selectBet", this._onBetClick, this);

    this.cheatButton = new PIXI.Sprite(
      GameAssets.textures.uiAtlas.textures["buyButton.webp"],
    );
    this.cheatButton.anchor.set(0.5, 0.5);
    this.cheatButton.interactive = true;
    this.cheatButton.on("pointerup", this._onCheatClick, this);

    this.buyButton = new PIXI.Sprite(GameAssets.textures.buy);
    this.buyButton.anchor.set(0.5, 0.5);
    this.buyButton.scale.set(0.5);
    this.buyButton.interactive = true;
    this.buyButton.on("pointerup", this._onBuy, this);

    this.homeButton = new PIXI.Sprite(GameAssets.textures.homeButton);
    this.homeButton.anchor.set(0.5, 0.5);

    this.infoButton = new PIXI.Sprite(
      GameAssets.textures.uiAtlas.textures["info.webp"],
    );
    this.infoButton.scale.set(0.5);
    this.infoButton.anchor.set(0.5, 0.5);
    this.infoButton.interactive = true;
    this.infoButton.on(
      "pointerup",
      () => {
        InfoPopup.show();
        this._onInfoClick();
      },
      this,
    );

    this.questionButton = new PIXI.Sprite(GameAssets.textures.questionButton);
    this.questionButton.anchor.set(0.5, 0.5);

    this.menuButton = new PIXI.Sprite(GameAssets.textures.menuButton);
    this.menuButton.anchor.set(0.5, 0.5);

    this.content.addChild(this.autoplayButton);
    this.content.addChild(this.fastButton);
    this.content.addChild(this.soundButton);
    this.content.addChild(this.spinButton);
    this.content.addChild(this.selectBetButton);
    this.content.addChild(this.cheatButton);
    this.content.addChild(this.buyButton);

    // this.addChild(this.homeButton);
    this.content.addChild(this.infoButton);
    // this.addChild(this.questionButton);

    // this.addChild(this.soundButton);
    // this.addChild(this.menuButton);

    // Create and display the popup
    this.betPopup = new BetPopup(4); // 3 rows, 3 columns
    this.betPopup.position.set(0, 0); // Centered in the app (optional)
    this.betPopup.setBetAmounts(
      GameEngine.getInstance().getLoadResponse().bets,
    );
    this.betPopup.on("betUpdated", this.updateBet, this);
    this.addChild(this.betPopup);

    InfoPopup.init();
    document.addEventListener("infoPopupClosed", () => {
      this._onInfoHide();
    });

    this.cheatPopup = new CheatPopup(); // 3 rows, 3 columns
    this.cheatPopup.position.set(0, 0); // Centered in the app (optional)
    this.cheatPopup.on("rigSelected", this.rigSelected, this);
    this.addChild(this.cheatPopup);

    this.autoplayPopup = new AutoplayPopup(3); // 3 rows, 3 columns
    this.autoplayPopup.position.set(0, 0); // Centered in the app (optional)
    this.autoplayPopup.setData([10, 20, 30, 50, 100, 250, 500, -1]);
    this.autoplayPopup.on("autoplaySelected", this.autoplaySelected, this);
    this.addChild(this.autoplayPopup);

    this.betPopup.hide();
    this.cheatPopup.hide();
    this.autoplayPopup.hide();

    const buttons: PIXI.Sprite[] = [
      this.cheatButton,
      this.infoButton,
      this.fastButton.Sprite,
      // this.homeButton,
      // this.menuButton,
      this.autoplayButton.Sprite,
      this.soundButton.Sprite,
      this.selectBetButton.Sprite,
      // this.questionButton,
      this.spinButton.Sprite,
    ];

    const specialButtons: PIXI.Sprite[] = [];

    for (let button of buttons) {
      button.on(
        "pointerup",
        () => {
          document.dispatchEvent(this.buttonClickEvent);
        },
        this,
      );
    }

    for (let button of specialButtons) {
      button.on(
        "pointerup",
        () => {
          document.dispatchEvent(this.specialButtonClickEvent);
        },
        this,
      );
    }

    this.onLandscape(1.5);
    this._changeState(UIState.SPIN);
  }

  hideBar() {
    this.menuBar.visible = false;
  }

  showBar() {
    this.menuBar.visible = true;
  }

  lock() {
    this._changeState(UIState.LOCK);
  }

  onScreenClick() {
    switch (this.currentState) {
      case UIState.LOCK:
        this.emit(UIEvent.SCREEN_CLICK);
        break;
      case UIState.SPIN:
        break;
      case UIState.STOP:
        this._onStop();
        break;
      case UIState.PASSIVE:
        break;
      case UIState.BET_POPUP:
        this._changeState(UIState.SPIN);
        break;
      case UIState.CHEAT_POPUP:
        this._changeState(UIState.SPIN);
        break;
      case UIState.AUTOPLAY_POPUP:
        this.autoplayAmount = 0;
        this.spinButton.setAutoplay(this.autoplayAmount);
        this._changeState(UIState.SPIN);
        break;
    }
  }

  handleKeyDown(event: any) {
    switch (event.code) {
      case "Space":
      case "Enter":
        switch (this.currentState) {
          case UIState.LOCK:
            this.emit(UIEvent.SCREEN_CLICK);
            this.autoplayPopup.hide();
            this.betPopup.hide();
            this.cheatPopup.hide();
            break;
          case UIState.SPIN:
            this.autoplayPopup.hide();
            this.betPopup.hide();
            this.cheatPopup.hide();
            this.spinButton.onClick();
            break;
          case UIState.STOP:
            this._onStop();
            break;
          case UIState.PASSIVE:
            break;
        }
        break;
    }
  }

  private _changeState(newState: UIState) {
    if (this.currentState === newState) return; // No change if the same state

    // Enter logic for the new state
    this.currentState = newState;
    switch (this.currentState) {
      case UIState.LOCK:
        this.spinButton.visible = false;
        this.autoplayButton.visible = false;
        this.selectBetButton.disable();
        this.cheatButton.visible = false;
        this.buyButton.visible = false;
        break;
      case UIState.INFO_POPUP:
        this.autoplayPopup.hide();
        this.betPopup.hide();
        this.cheatPopup.hide();
        break;
      case UIState.SPIN:
        this.autoplayPopup.hide();
        this.betPopup.hide();
        this.cheatPopup.hide();
        if (this.isFreespins) {
          this.autoplayButton.visible = false;
          this.selectBetButton.disable();
          this.cheatButton.visible = false;
          this.buyButton.visible = false;
        } else {
          this.autoplayButton.visible = true;
          this.selectBetButton.enable();
          this.cheatButton.visible = true;
          this.buyButton.visible = true;
        }
        this.spinButton.visible = true;
        this.fastButton.visible = true;
        this.soundButton.visible = true;
        this.spinButton.resetButton();
        break;
      case UIState.STOP:
        this.autoplayButton.visible = false;
        // this.fastButton.visible = false;
        // this.soundButton.visible = false;
        this.selectBetButton.disable();
        this.cheatButton.visible = false;
        this.buyButton.visible = false;

        this.betPopup.hide();
        this.cheatPopup.hide();
        this.autoplayPopup.hide();
        break;
      case UIState.BET_POPUP:
        this.betPopup.show();
        this.cheatPopup.hide();
        this.autoplayPopup.hide();
        break;
      case UIState.AUTOPLAY_POPUP:
        this.autoplayPopup.show();
        this.betPopup.hide();
        this.cheatPopup.hide();
        break;
      case UIState.CHEAT_POPUP:
        this.cheatPopup.show();
        this.betPopup.hide();
        this.autoplayPopup.hide();
        break;
    }
  }

  onPortrait(data?: any) {
    this.bottomBar.y = 800;
    this.bottomBar.texture =
      GameAssets.textures.uiAtlas.textures["bottomBarPortrait.webp"];
    this.bottomBar.width = 1920 * data.ratio;

    this.balanceContent.onPortrait();
    this.balanceContent.setFontSize(40);
    this.balanceContent.position.set(-320, 880);
    this.balanceContent.scale.set(1);
    this.winContent.onPortrait();
    this.winContent.setFontSize(40);
    this.winContent.position.set(0, 880);
    this.winContent.scale.set(1);
    this.selectBetButton.onPortrait();
    this.selectBetButton.scale.set(1);
    this.selectBetButton.position.set(350, 880);

    const offsetY: number = 650;
    this.betPopup.scale.set(1.3);
    this.autoplayPopup.scale.set(1.3);

    this.autoplayButton.scale.set(2);
    this.fastButton.scale.set(2);
    this.soundButton.scale.set(2);
    this.spinButton.scale.set(1.5);
    this.cheatButton.scale.set(1.3);
    this.autoplayButton.position.set(-210, offsetY);
    this.fastButton.position.set(380, offsetY);
    this.soundButton.position.set(-390, offsetY);
    this.spinButton.position.set(0, offsetY);
    this.cheatButton.position.set(220, offsetY);
    // this.buyButton.position.set(-300, -800);

    const bottomY: number = 740;
    this.homeButton.position.set(-350, bottomY);
    this.infoButton.position.set(-300, bottomY);
    this.infoButton.scale.set(0.5);
    this.questionButton.position.set(-190, bottomY);

    this.menuButton.position.set(190, bottomY);

    this.bottomBlackCoverArea.clear();
    this.bottomBlackCoverArea
      .rect(-2048, 950, 4096, 512)
      .fill({ color: 0x000000, alpha: 2 });
  }

  onLandscape(data?: any) {
    this.bottomBar.texture =
      GameAssets.textures.uiAtlas.textures["bottomBar.webp"];
    this.bottomBar.width = 2600;

    const k = Math.max(1.7 / data.ratio, 1) * 1.1;
    const xOffset: number = 700;

    this.betPopup.scale.set(1);
    this.autoplayPopup.scale.set(1);
    this.autoplayButton.scale.set(1);
    this.fastButton.scale.set(1);
    this.soundButton.scale.set(1);
    this.spinButton.scale.set(1);
    this.cheatButton.scale.set(1);
    this.fastButton.position.set(550 / k, 500);
    this.soundButton.position.set(-800 / k, 450);
    this.infoButton.position.set(-890 / k, 450);
    this.infoButton.scale.set(0.32);
    this.autoplayButton.position.set(650 / k, 500);
    this.spinButton.position.set(900 / k, 450);
    this.selectBetButton.position.set(340 / k, 515);
    this.selectBetButton.scale.set(0.7);
    this.selectBetButton.onLandscape();
    this.cheatButton.position.set(900 / k, 300);

    this.winContent.onLandscape();
    this.balanceContent.onLandscape();

    this.bottomBar.y = 470;
    this.bottomBar.texture =
      GameAssets.textures.uiAtlas.textures["bottomBar.webp"];
    this.balanceContent.position.set(-750 / k, 515);
    this.balanceContent.setFontSize(40);
    this.balanceContent.scale.set(0.7);
    this.winContent.setFontSize(40);
    this.winContent.position.set(0, 515);
    this.winContent.scale.set(0.7);

    this.bottomBlackCoverArea.clear();
    this.bottomBlackCoverArea
      .rect(-2048, 540, 4096, 512)
      .fill({ color: 0x000000, alpha: 2 });
  }

  private _onBetClick() {
    if (
      this.currentState === UIState.SPIN ||
      this.currentState === UIState.AUTOPLAY_POPUP
    ) {
      this._changeState(UIState.BET_POPUP);
    }
  }

  private _onCheatClick() {
    if (
      this.currentState === UIState.SPIN ||
      this.currentState === UIState.AUTOPLAY_POPUP
    ) {
      this._changeState(UIState.CHEAT_POPUP);
    }
  }

  private _onAutoplayClick() {
    if (
      this.currentState === UIState.SPIN ||
      this.currentState === UIState.BET_POPUP
    ) {
      this._changeState(UIState.AUTOPLAY_POPUP);
    }
  }

  private _onInfoClick() {
    this._changeState(UIState.INFO_POPUP);
  }

  private _onInfoHide() {
    this._changeState(UIState.SPIN);
  }

  private _onBuy() {
    this._changeState(UIState.STOP);
    this.spinButton.setPassive();
    this.emit("buy");
  }

  private _onSpin() {
    this._changeState(UIState.STOP);
    this.emit("spin");

    if (GlobalState.getInstance().fastOn) {
      this._onStop();
    }
  }

  private _onAutoplayStart() {
    this._changeState(UIState.STOP);
  }

  private _onAutoplayStop() {
    this._changeState(UIState.STOP);
  }

  private _onStop() {
    this.emit("stop");
    this._changeState(UIState.PASSIVE);
  }

  public resetSpinButton(isFreespins: boolean = false) {
    this.isFreespins = isFreespins;
    this._changeState(UIState.SPIN);
  }

  public setStopButton() {
    this._changeState(UIState.STOP);
  }

  public resize(data: any) {
    if (data.ratio < 1.4) {
      this.onPortrait(data);
    } else {
      this.onLandscape(data);
    }
  }

  public updateBet() {
    document.dispatchEvent(this.buttonClickEvent);
    this.selectBetButton.setBet(
      GameEngine.getInstance()
        .getFormatter()
        .format(GlobalState.getInstance().bet),
    );
  }

  public rigSelected(data: string) {
    document.dispatchEvent(this.buttonClickEvent);
    observer.publish("rigSelected", data);
    this._changeState(UIState.SPIN);
    this.spinButton.onClick();
  }

  public autoplaySelected(amount: number) {
    document.dispatchEvent(this.buttonClickEvent);
    this.autoplayAmount = amount;
    this.spinButton.setAutoplay(amount);
  }

  public updateVisualBalance() {
    this.balanceContent.setText(
      GameEngine.getInstance()
        .getFormatter()
        .format(GlobalState.getInstance().balance),
    );
  }

  public setWin(win: number) {
    this.winContent.setText(
      GameEngine.getInstance().getFormatter().format(win),
    );
  }
}
